import { useEffect, useRef, useState } from "react";
import { currency, toOption } from "../lib/format.js";
import { EmployeeLink } from "../components/recordLinks.jsx";
import { AsyncActionButton, Panel, ResourceTable, SelectField, SimpleForm, TextField, Topbar } from "../components/ui.jsx";

export function IncentivesView({ resources, users, user, onCreate, onUpdate, openEmployee, onAddEmployee }) {
  const reps = users.filter((item) => item.role === "sales" && item.active !== false);
  const partnerManagers = users.filter((item) => ["manager", "admin", "support"].includes(item.role) && item.active !== false);
  const hasActiveSalesReps = reps.length > 0;
  const hasPartnerManagers = partnerManagers.length > 0;
  const repOptions = hasActiveSalesReps ? reps.map(toOption) : [{ value: "", label: "No active sales reps available" }];
  const partnerManagerOptions = hasPartnerManagers ? partnerManagers.map(toOption) : [{ value: "", label: "No managers available" }];
  const hasChannelPartners = resources.channelPartners.length > 0;
  const channelPartnerOptions = hasChannelPartners
    ? resources.channelPartners.map((partner) => ({ value: partner.id, label: partner.name }))
    : [{ value: "", label: "No channel partners available" }];
  const [form, setForm] = useState({ userId: "" });
  const [channelForm, setChannelForm] = useState({ partnerId: resources.channelPartners[0]?.id ?? "", salesValue: resources.channelPartners[0]?.salesValue ?? 0, productMix: "Mixed" });
  const [partnerForm, setPartnerForm] = useState(emptyPartnerForm());
  const [editingPartnerId, setEditingPartnerId] = useState("");
  const partnerEditorRef = useRef(null);
  const incentiveFormRef = useRef(null);
  const channelFormRef = useRef(null);
  const channelPartnerIds = resources.channelPartners.map((partner) => partner.id).join("|");
  const partnerManagerIds = partnerManagers.map((manager) => manager.id).join("|");
  const canManageChannelPartners = user.role === "manager" || user.role === "admin";

  useEffect(() => {
    const fallbackRepId = reps[0]?.id || "";
    setForm((current) => (reps.some((rep) => rep.id === current.userId) ? current : { ...current, userId: fallbackRepId }));
  }, [reps.map((rep) => rep.id).join("|")]);

  useEffect(() => {
    const fallbackPartner = resources.channelPartners[0];
    setChannelForm((current) => {
      const partner = resources.channelPartners.find((item) => item.id === current.partnerId);
      if (partner) return current;
      return {
        ...current,
        partnerId: fallbackPartner?.id || "",
        salesValue: fallbackPartner?.salesValue ?? current.salesValue,
      };
    });
  }, [channelPartnerIds]);

  useEffect(() => {
    const fallbackManagerId = partnerManagers[0]?.id || "";
    setPartnerForm((current) =>
      partnerManagers.some((manager) => manager.id === current.managerId)
        ? current
        : { ...current, managerId: fallbackManagerId },
    );
  }, [partnerManagerIds]);

  return (
    <>
      <Topbar title="Incentives" subtitle="Sales rep and channel partner incentive calculation, approval, and payout tracking." />
      <section className="content-grid">
        {canManageChannelPartners ? (
          <div className="product-editor-anchor" ref={partnerEditorRef}>
            <Panel
              title={editingPartnerId ? "Edit Channel Partner" : "Add Channel Partner"}
              subtitle="Keep partner ownership connected to the manager who owns the relationship."
            >
              {!hasPartnerManagers ? <div className="notice">Add an active manager, admin, or support lead before saving a channel partner.</div> : null}
              <SimpleForm disabled={!hasPartnerManagers} onSubmit={saveChannelPartner} submitLabel={editingPartnerId ? "Update Partner" : "Save Partner"} submittingLabel={editingPartnerId ? "Updating..." : "Saving..."}>
                <TextField label="Partner name" value={partnerForm.name} required onChange={(name) => setPartnerForm({ ...partnerForm, name })} />
                <TextField label="Partner type" value={partnerForm.type} onChange={(type) => setPartnerForm({ ...partnerForm, type })} />
                <SelectField
                  label="Relationship manager"
                  value={partnerForm.managerId}
                  options={partnerManagerOptions}
                  disabled={!hasPartnerManagers}
                  actionLabel="Onboard employee"
                  onAction={user.role === "admin" ? onAddEmployee : undefined}
                  secondaryActionLabel="Open selected employee"
                  secondaryActionDisabled={!partnerForm.managerId}
                  onSecondaryAction={openEmployee ? () => openEmployee(partnerForm.managerId) : undefined}
                  onChange={(managerId) => setPartnerForm({ ...partnerForm, managerId })}
                />
                <TextField label="Sales value" type="number" value={partnerForm.salesValue} onChange={(salesValue) => setPartnerForm({ ...partnerForm, salesValue })} />
                <TextField label="Product mix" value={partnerForm.productMix} onChange={(productMix) => setPartnerForm({ ...partnerForm, productMix })} />
                <TextField label="Loyalty tier" value={partnerForm.loyaltyTier} onChange={(loyaltyTier) => setPartnerForm({ ...partnerForm, loyaltyTier })} />
                {editingPartnerId ? (
                  <div className="row-actions full">
                    <button className="ghost-btn" type="button" onClick={resetPartnerForm}>
                      Cancel Edit
                    </button>
                  </div>
                ) : null}
              </SimpleForm>
            </Panel>
          </div>
        ) : null}
        <div ref={incentiveFormRef}>
          <Panel title="Calculate Incentive">
            {!hasActiveSalesReps ? <div className="notice">No active sales reps are available in this reporting scope. Add or reactivate a sales rep before calculating incentives.</div> : null}
            <SimpleForm disabled={!hasActiveSalesReps} onSubmit={() => onCreate("/incentives/calculate", form)}>
              <SelectField
                label="Sales executive"
                value={form.userId}
                options={repOptions}
                disabled={user.role === "sales" || !hasActiveSalesReps}
                actionLabel="Onboard employee"
                onAction={onAddEmployee}
                secondaryActionLabel="Open selected employee"
                secondaryActionDisabled={!form.userId}
                onSecondaryAction={openEmployee ? () => openEmployee(form.userId) : undefined}
                onChange={(userId) => setForm({ userId })}
              />
            </SimpleForm>
          </Panel>
        </div>
        <Panel title="Payouts">
          <ResourceTable
            rows={resources.incentives}
            columns={[["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />], ["basis", "Basis"], ["sales", "Sales", currency], ["margin", "Margin", currency], ["payoutAmount", "Payout", currency], ["status", "Status"]]}
            action={(row) => user.role === "manager" || user.role === "admin" ? (
              <div className="row-actions">
                <button className="mini-btn" type="button" disabled={!canUseRep(row.userId)} onClick={() => startRepIncentiveFromPayout(row)}>
                  Calculate Again
                </button>
                <AsyncActionButton
                  pendingLabel={row.status === "Approved" ? "Paying..." : "Approving..."}
                  errorMessage="We could not update this payout. Please try again."
                  onAction={() => onUpdate(`/incentives/${row.id}/status`, { status: row.status === "Paid" ? "Paid" : row.status === "Approved" ? "Paid" : "Approved" })}
                >
                  {row.status === "Approved" ? "Pay" : "Approve"}
                </AsyncActionButton>
              </div>
            ) : null}
          />
        </Panel>
      </section>
      <section className="content-grid spaced">
        <div ref={channelFormRef}>
          <Panel title="Channel Incentive">
            {!hasChannelPartners ? <div className="notice">No channel partners are available. Add a channel partner before calculating a partner incentive.</div> : null}
            <SimpleForm disabled={!hasChannelPartners} onSubmit={() => onCreate("/channel-incentives/calculate", channelForm)}>
              <SelectField
                label="Channel partner"
                value={channelForm.partnerId}
                options={channelPartnerOptions}
                disabled={!hasChannelPartners}
                actionLabel="Add channel partner"
                onAction={canManageChannelPartners ? focusPartnerEditor : undefined}
                secondaryActionLabel="Edit selected partner"
                secondaryActionDisabled={!channelForm.partnerId}
                onSecondaryAction={canManageChannelPartners ? () => editSelectedPartner(channelForm.partnerId) : undefined}
                onChange={(partnerId) => {
                  const partner = resources.channelPartners.find((item) => item.id === partnerId);
                  setChannelForm({
                    ...channelForm,
                    partnerId,
                    salesValue: partner?.salesValue ?? channelForm.salesValue,
                    productMix: partner?.productMix ?? channelForm.productMix,
                  });
                }}
              />
              <TextField label="Sales value" type="number" value={channelForm.salesValue} onChange={(salesValue) => setChannelForm({ ...channelForm, salesValue })} />
              <TextField label="Product mix" value={channelForm.productMix} onChange={(productMix) => setChannelForm({ ...channelForm, productMix })} />
            </SimpleForm>
          </Panel>
        </div>
        <Panel title="Channel Payouts">
          <ResourceTable
            rows={resources.channelIncentives}
            columns={[["partnerName", "Partner", renderPartnerReference], ["scheme", "Scheme"], ["salesValue", "Sales", currency], ["productMix", "Product Mix"], ["payoutAmount", "Payout", currency], ["status", "Status"]]}
            action={canManageChannelPartners ? (row) => (
              <button className="mini-btn" type="button" disabled={!partnerFromReference(row)} onClick={() => startChannelIncentiveFromPayout(row)}>
                Calculate Again
              </button>
            ) : null}
          />
        </Panel>
        <Panel title="Channel Partner Directory">
          <ResourceTable
            rows={resources.channelPartners}
            columns={[
              ["name", "Partner", (value, row) => <PartnerLink partner={row} partnerName={value} canOpen={canManageChannelPartners} onOpen={startPartnerEdit} />],
              ["type", "Type"],
              ["managerId", "Manager", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
              ["salesValue", "Sales", currency],
              ["productMix", "Product Mix"],
              ["loyaltyTier", "Tier"],
            ]}
            action={canManageChannelPartners ? (row) => (
              <div className="row-actions">
                <button className="mini-btn" type="button" onClick={() => startChannelIncentiveFromPartner(row)}>
                  Calculate
                </button>
                <button className="mini-btn" type="button" onClick={() => startPartnerEdit(row)}>
                  Edit
                </button>
              </div>
            ) : null}
          />
        </Panel>
      </section>
    </>
  );

  function renderPartnerReference(value, row) {
    const partner = resources.channelPartners.find((item) => item.id === row.partnerId || item.name === value);
    return <PartnerLink partner={partner} partnerName={value} canOpen={canManageChannelPartners} onOpen={startPartnerEdit} />;
  }

  async function saveChannelPartner() {
    const manager = partnerManagers.find((item) => item.id === partnerForm.managerId);
    const payload = {
      ...partnerForm,
      teamId: manager?.teamId || "",
    };
    const saved = editingPartnerId
      ? await onUpdate(`/channel-partners/${editingPartnerId}`, payload)
      : await onCreate("/channel-partners", payload);
    if (saved === false) throw new Error("Save failed");
    resetPartnerForm();
  }

  function editSelectedPartner(partnerId) {
    const partner = resources.channelPartners.find((item) => item.id === partnerId);
    if (partner) startPartnerEdit(partner);
  }

  function startPartnerEdit(partner) {
    setEditingPartnerId(partner.id);
    setPartnerForm({
      name: partner.name || "",
      type: partner.type || "Distributor",
      managerId: partner.managerId || partnerManagers[0]?.id || "",
      salesValue: partner.salesValue ?? 0,
      productMix: partner.productMix || "Mixed",
      loyaltyTier: partner.loyaltyTier || "Standard",
    });
    focusPartnerEditorAfterRender();
  }

  function startChannelIncentiveFromPartner(partner) {
    setChannelForm({
      partnerId: partner.id,
      salesValue: partner.salesValue ?? 0,
      productMix: partner.productMix || "Mixed",
    });
    focusForm(channelFormRef);
  }

  function startRepIncentiveFromPayout(row) {
    if (!canUseRep(row.userId)) return;
    setForm({ userId: row.userId });
    focusForm(incentiveFormRef);
  }

  function startChannelIncentiveFromPayout(row) {
    const partner = partnerFromReference(row);
    if (!partner) return;
    setChannelForm({
      partnerId: partner.id,
      salesValue: row.salesValue ?? partner.salesValue ?? 0,
      productMix: row.productMix || partner.productMix || "Mixed",
    });
    focusForm(channelFormRef);
  }

  function canUseRep(userId) {
    return reps.some((rep) => rep.id === userId);
  }

  function partnerFromReference(row) {
    return resources.channelPartners.find((item) => item.id === row.partnerId || item.name === row.partnerName) || null;
  }

  function focusForm(ref) {
    window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      ref.current?.querySelector("select, input")?.focus({ preventScroll: true });
    }, 0);
  }

  function focusPartnerEditor() {
    setEditingPartnerId("");
    setPartnerForm(emptyPartnerForm(partnerManagers[0]?.id || ""));
    focusPartnerEditorAfterRender();
  }

  function focusPartnerEditorAfterRender() {
    window.setTimeout(() => {
      partnerEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      partnerEditorRef.current?.querySelector("input")?.focus({ preventScroll: true });
    }, 0);
  }

  function resetPartnerForm() {
    setEditingPartnerId("");
    setPartnerForm(emptyPartnerForm(partnerManagers[0]?.id || ""));
  }
}

function emptyPartnerForm(managerId = "") {
  return { name: "", type: "Distributor", managerId, salesValue: 0, productMix: "Mixed", loyaltyTier: "Standard" };
}

function PartnerLink({ partner, partnerName, canOpen, onOpen }) {
  const label = partnerName || partner?.name || "-";
  if (!canOpen || !partner) return label;
  return (
    <button className="table-link-btn" type="button" onClick={() => onOpen(partner)}>
      {label}
    </button>
  );
}
